import { useEffect, useState } from 'react';
// Types
import type { CaptionProps } from 'react-day-picker';
import { useDayPicker, useNavigation } from 'react-day-picker';

import dayjs from 'dayjs';

// Components
import Icon from '@/components/base/Icon';

type SelectYearProps = Pick<CaptionProps, 'displayMonth'>;

export function SelectYear({ displayMonth }: SelectYearProps) {
    // Can be a empty string to avoid some problems when the use remove all the date
    const [selectedYear, setSelectedYear] = useState<number | ''>(dayjs(displayMonth).get('year'));
    const { fromDate, toDate } = useDayPicker();

    const { goToMonth } = useNavigation();

    const minDateYear = fromDate ? dayjs(fromDate).get('year') : null;
    const maxDateYear = toDate ? dayjs(toDate).get('year') : null;

    const updateDate = (newYear: Date) => {
        if (newYear < fromDate) {
            return goToMonth(fromDate);
        }

        if (newYear > toDate) {
            return goToMonth(toDate);
        }

        goToMonth(newYear);
    };

    const incrementYear = () => {
        const newYear = dayjs(displayMonth).add(1, 'year').format();
        updateDate(new Date(newYear));
    };

    const decrementYear = () => {
        const newYear = dayjs(displayMonth).subtract(1, 'year').format();
        updateDate(new Date(newYear));
    };

    useEffect(() => {
        const minYear = minDateYear ?? 2000;
        const maxYear = maxDateYear ?? 9999;

        if (selectedYear && selectedYear >= minYear && selectedYear <= maxYear) {
            const newYear = dayjs(displayMonth).set('year', selectedYear).format();
            updateDate(new Date(newYear));
        }
    }, [selectedYear]);

    useEffect(() => {
        const currentYear = dayjs(displayMonth).get('year');
        if (currentYear !== selectedYear) {
            setSelectedYear(currentYear);
        }
    }, [displayMonth]);

    return (
        <div className="flex items-center">
            <input
                type="number"
                className="w-11 border-0 bg-transparent p-0 text-sm focus:ring-0 focus:ring-offset-0"
                min={minDateYear}
                max={maxDateYear}
                value={selectedYear}
                data-testid="datepicker-input-year"
                onChange={(e) => {
                    setSelectedYear(e.target.value ? Number(e.target.value) : '');
                }}
            />
            <div className="flex flex-col text-8 text-icoGray-400 dark:text-icoDarkMode-wolf">
                <button
                    onClick={incrementYear}
                    data-testid="datepicker-next-year-button"
                    className="flex items-center focus:ring-0 focus:ring-offset-0 disabled:opacity-40"
                    disabled={Boolean(maxDateYear && selectedYear && selectedYear >= maxDateYear)}>
                    <Icon icon="chevron-top" />
                </button>
                <button
                    onClick={decrementYear}
                    data-testid="datepicker-previous-year-button"
                    className="flex items-center focus:ring-0 focus:ring-offset-0 disabled:opacity-40"
                    disabled={Boolean(minDateYear && selectedYear && selectedYear <= minDateYear)}>
                    <Icon icon="chevron-bottom" />
                </button>
            </div>
        </div>
    );
}
